import { Controller } from "@hotwired/stimulus"
import AutocompleteAddress from "client/javascripts/vendor/AutocompleteAddress"

const COUNTRY_TO_CONTEXT = {
  NL: "nld",
  BE: "bel",
  DE: "deu",
}

export default class extends Controller {
  static targets = ["street", "streetNo", "zipcode", "city", "countryCode"]

  connect() {
    this.initAutocomplete()
    this.bindNlLookup()
    this.bindCountryChange()
  }

  disconnect() {
    if (this.autocomplete) {
      this.autocomplete.destroy()
      this.autocomplete = null
    }
  }

  // --- Autocomplete Dropdown on Postcode Field ---

  initAutocomplete() {
    if (!this.hasZipcodeTarget) return

    this.autocomplete = new AutocompleteAddress(this.zipcodeTarget, {
      autocompleteUrl: "/api/2/postcodes/autocomplete",
      addressDetailsUrl: "/api/2/postcodes/address",
      context: this.currentContext(),
      appendTo: document.body,
    })

    this.zipcodeTarget.addEventListener("input", () => {
      if (this.autocomplete) {
        this.autocomplete.setCountry(this.currentContext())
      }
    })

    this.zipcodeTarget.addEventListener("autocomplete-select", (e) => {
      if (e.detail.precision === "Address") {
        this.autocomplete.getDetails(e.detail.context, (details) => {
          this.fillAddress(details)
        })
      }
    })

    this.zipcodeTarget.addEventListener("autocomplete-error", (e) => {
      console.error("Postcode.eu autocomplete error:", e.detail)
    })
  }

  fillAddress(details) {
    const address = details.address
    const country = details.country

    this.unlockFields()
    this.clearError()

    if (this.hasStreetTarget) {
      this.streetTarget.value = address.street || ""
    }

    if (this.hasStreetNoTarget) {
      let streetNo = ""
      if (address.buildingNumber) {
        streetNo = String(address.buildingNumber)
        if (address.buildingNumberAddition) {
          streetNo += " " + address.buildingNumberAddition
        }
      }
      this.streetNoTarget.value = streetNo
    }

    if (this.hasZipcodeTarget) {
      this.zipcodeTarget.value = address.postcode || ""
    }

    if (this.hasCityTarget) {
      this.cityTarget.value = address.locality || ""
    }

    if (this.hasCountryCodeTarget && country) {
      const iso2 = country.iso2Code
      if (iso2) {
        this.countryCodeTarget.value = iso2
      }
    }

    setTimeout(() => this.lockFields(), 500)
  }

  // --- NL Postcode + House Number Lookup (fallback) ---

  bindNlLookup() {
    if (this.hasStreetNoTarget) {
      this.streetNoTarget.addEventListener("change", () => this.tryNlLookup())
      this.streetNoTarget.addEventListener("blur", () => this.tryNlLookup())
    }
  }

  async tryNlLookup() {
    if (!this.hasCountryCodeTarget || this.countryCodeTarget.value !== "NL") return
    if (!this.hasZipcodeTarget || !this.hasStreetNoTarget) return

    const zipcode = this.zipcodeTarget.value.trim()
    const streetNo = this.streetNoTarget.value.trim()

    if (!zipcode || !streetNo) return
    if (!zipcode.match(/^\d{4}\s?[A-Za-z]{2}$/)) return

    const lookupKey = `${zipcode}-${streetNo}`
    if (this.lastNlLookup === lookupKey) return
    this.lastNlLookup = lookupKey

    try {
      const params = new URLSearchParams({
        postcode: zipcode.replace(/\s/g, ""),
        house_number: streetNo,
      })

      const response = await fetch(`/api/2/postcodes/nl_lookup?${params}`, {
        headers: { "Accept": "application/json" },
      })

      if (!response.ok) {
        this.showFeedback(false)
        this.unlockFields()
        this.showError(window.i18n.t("address_autocomplete.address_not_found"))
        return
      }

      const data = await response.json()

      if (data.street && data.city) {
        this.unlockFields()
        this.clearError()
        if (this.hasStreetTarget) this.streetTarget.value = data.street
        if (this.hasCityTarget) this.cityTarget.value = data.city
        this.showFeedback(true)
        setTimeout(() => this.lockFields(), 500)
      } else {
        this.showFeedback(false)
        this.unlockFields()
        this.showError(window.i18n.t("address_autocomplete.address_not_found"))
      }
    } catch {
      this.showFeedback(false)
      this.unlockFields()
    }
  }

  // --- Fill & Lock ---

  lockFields() {
    const targets = [
      this.hasStreetTarget && this.streetTarget,
      this.hasCityTarget && this.cityTarget,
    ]
    targets.filter(Boolean).forEach((el) => {
      el.readOnly = true
      el.classList.add("autocomplete-readonly")
    })
  }

  unlockFields() {
    const targets = [
      this.hasStreetTarget && this.streetTarget,
      this.hasCityTarget && this.cityTarget,
    ]
    targets.filter(Boolean).forEach((el) => {
      el.readOnly = false
      el.classList.remove("autocomplete-readonly")
    })
  }

  // --- Country Change ---

  bindCountryChange() {
    if (!this.hasCountryCodeTarget) return

    this.countryCodeTarget.addEventListener("change", () => {
      const context = this.currentContext()

      if (this.autocomplete) {
        this.autocomplete.setCountry(context)
      }

      this.unlockFields()
      if (this.hasStreetTarget) this.streetTarget.value = ""
      if (this.hasStreetNoTarget) this.streetNoTarget.value = ""
      if (this.hasZipcodeTarget) this.zipcodeTarget.value = ""
      if (this.hasCityTarget) this.cityTarget.value = ""
      this.lastNlLookup = null
    })
  }

  // --- Helpers ---

  currentContext() {
    if (this.hasCountryCodeTarget) {
      const code = this.countryCodeTarget.value
      return COUNTRY_TO_CONTEXT[code] || "nld"
    }
    return "nld"
  }

  showError(message) {
    this.clearError()
    const error = document.createElement("div")
    error.classList.add("address-error", "text-danger", "small", "mt-1")
    error.textContent = message
    if (this.hasStreetNoTarget) {
      this.streetNoTarget.closest(".col-12")?.appendChild(error)
      this.streetNoTarget.classList.add("is-invalid")
    }
  }

  clearError() {
    const existing = this.element.querySelector(".address-error")
    if (existing) existing.remove()
    if (this.hasStreetNoTarget) {
      this.streetNoTarget.classList.remove("is-invalid")
    }
  }

  showFeedback(success) {
    const existing = this.element.querySelector(".nl-lookup-feedback")
    if (existing) existing.remove()

    const feedback = document.createElement("div")
    feedback.classList.add("nl-lookup-feedback", success ? "nl-lookup-success" : "nl-lookup-error")
    feedback.textContent = success ? "\u2713" : "\u2717"

    if (this.hasZipcodeTarget) {
      this.zipcodeTarget.closest(".col-12")?.appendChild(feedback)
    }

    setTimeout(() => feedback.remove(), 3000)
  }
}
